import React from "react";

const STORAGE_KEY = "learning_drafts_v1";
const FORMAT_VERSION = 1;
const MAX_DRAFTS = 12;
const MAX_DRAFT_BYTES = 700_000;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const ELEMENT_MARKER = "__matematronReactElement";
const SPECIAL_NUMBER_MARKER = "__matematronNumber";

function safeNow() {
  return Date.now();
}

function encodeValue(value, seen = new WeakSet()) {
  if (typeof value === "number" && !Number.isFinite(value)) {
    return { [SPECIAL_NUMBER_MARKER]: String(value) };
  }
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "function" || typeof value === "symbol") return undefined;
  if (React.isValidElement(value)) {
    const elementType =
      typeof value.type === "string"
        ? { kind: "host", name: value.type }
        : value.type === React.Fragment
          ? { kind: "fragment", name: "Fragment" }
          : { kind: "component", name: value.type?.displayName || value.type?.name || "Unknown" };
    return {
      [ELEMENT_MARKER]: true,
      elementType,
      key: value.key == null ? null : String(value.key),
      props: encodeValue(value.props, seen),
    };
  }
  if (typeof value !== "object") return value;
  if (seen.has(value)) return undefined;
  seen.add(value);
  const output = Array.isArray(value) ? [] : {};
  Object.entries(value).forEach(([key, entry]) => {
    const encoded = encodeValue(entry, seen);
    if (encoded !== undefined) output[key] = encoded;
  });
  seen.delete(value);
  return output;
}

function decodeValue(value, componentResolver = {}) {
  if (!value || typeof value !== "object") return value;
  if (SPECIAL_NUMBER_MARKER in value) {
    if (value[SPECIAL_NUMBER_MARKER] === "Infinity") return Infinity;
    if (value[SPECIAL_NUMBER_MARKER] === "-Infinity") return -Infinity;
    return NaN;
  }
  if (value[ELEMENT_MARKER]) {
    const descriptor = value.elementType || {};
    const props = decodeValue(value.props || {}, componentResolver);
    let type = React.Fragment;
    if (descriptor.kind === "host") type = descriptor.name;
    if (descriptor.kind === "component" && componentResolver[descriptor.name]) {
      type = componentResolver[descriptor.name];
    }
    return React.createElement(type, { ...props, key: value.key ?? undefined });
  }
  if (Array.isArray(value)) return value.map((entry) => decodeValue(entry, componentResolver));
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, decodeValue(entry, componentResolver)])
  );
}

function readStore() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function pruneStore(store, now = safeNow()) {
  return Object.fromEntries(
    Object.entries(store)
      .filter(([, entry]) => entry?.version === FORMAT_VERSION && now - Number(entry.updatedAt || 0) <= MAX_AGE_MS)
      .sort((a, b) => Number(b[1].updatedAt || 0) - Number(a[1].updatedAt || 0))
      .slice(0, MAX_DRAFTS)
  );
}

export function buildLearningDraftKey({ kind, userId, pathname, scope }) {
  return [kind || "learning", userId || "guest", pathname || "/", scope || "default"]
    .map((part) => encodeURIComponent(String(part)))
    .join("|");
}

export function readLearningDraft(key, componentResolver = {}) {
  if (!key) return null;
  const entry = pruneStore(readStore())[key];
  if (!entry) return null;
  try {
    return decodeValue(entry.payload, componentResolver);
  } catch {
    return null;
  }
}

export function writeLearningDraft(key, payload) {
  if (!key) return false;
  try {
    const encoded = encodeValue(payload);
    const entry = { version: FORMAT_VERSION, updatedAt: safeNow(), payload: encoded };
    if (JSON.stringify(entry).length > MAX_DRAFT_BYTES) return false;
    const store = pruneStore({ ...readStore(), [key]: entry });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
}

export function removeLearningDraft(key) {
  if (!key) return;
  try {
    const store = readStore();
    if (!(key in store)) return;
    delete store[key];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pruneStore(store)));
  } catch {
    // Brak localStorage nie może blokować nauki.
  }
}

function getAuthenticatedDraftPath(pathname, scope) {
  if (pathname.startsWith("/demo/matura/sciezka")) {
    return pathname.replace("/demo/matura/sciezka", "/matura/sciezka");
  }
  if (pathname === "/demo/arkusz-maturalny") {
    return `/matura/arkusze/${encodeURIComponent(scope)}`;
  }
  if (pathname.startsWith("/demo-temat/lekcja/")) {
    return pathname.replace("/demo-temat/lekcja/", "/lekcja/");
  }
  if (pathname.startsWith("/demo-temat/quiz/")) {
    return pathname.replace("/demo-temat/quiz/", "/quiz/");
  }
  return "";
}

export function migrateGuestLearningDraftsToUser(userId) {
  const normalizedUserId = String(userId || "").trim().toLowerCase();
  if (!normalizedUserId) return 0;
  try {
    const store = pruneStore(readStore());
    let migrated = 0;
    Object.entries(store).forEach(([key, entry]) => {
      const parts = key.split("|").map((part) => decodeURIComponent(part));
      if (parts.length !== 4) return;
      const [kind, draftUserId, pathname, scope] = parts;
      if (draftUserId !== "guest") return;
      const authenticatedPath = getAuthenticatedDraftPath(pathname, scope);
      if (!authenticatedPath) return;
      const targetKey = buildLearningDraftKey({
        kind,
        userId: normalizedUserId,
        pathname: authenticatedPath,
        scope,
      });
      const existing = store[targetKey];
      if (!existing || Number(entry.updatedAt || 0) >= Number(existing.updatedAt || 0)) {
        store[targetKey] = entry;
      }
      delete store[key];
      migrated += 1;
    });
    if (migrated) localStorage.setItem(STORAGE_KEY, JSON.stringify(pruneStore(store)));
    return migrated;
  } catch {
    return 0;
  }
}

// Zachowujemy dotychczasową nazwę dla starszych importów.
export const migrateGuestMaturaDraftsToUser = migrateGuestLearningDraftsToUser;

export const learningDraftStorageKey = STORAGE_KEY;
